import type { ProductionStepMappingRow } from "../types";

type ProductionStepAliases = Record<string, string[]>;

type ProductionStepConfigValue =
  | { aliases?: string[]; allowed?: boolean }
  | string[]
  | string;

type ProductionStepConfig = Record<string, ProductionStepConfigValue>;

type ProductionStepEntry = {
  aliases: string[];
  allowed: boolean;
};

type MatchCandidate = {
  step: string;
  score: number;
  tokenCount: number;
  charCount: number;
};

type AliasEntry = {
  tokens: string[];
  hasDigits: boolean;
  tokenCount: number;
  charCount: number;
};

const DEFAULT_STRICTNESS = 0.8;

const DEFAULT_PRODUCTION_STEP_CONFIG: ProductionStepConfig = {
  "Gob in Blank": {
    aliases: ["Gob_in_Blank", "Gob-in-Blank", "GOB in Blank", "GOB-in-Blank"],
    allowed: true
  },
  "Blank Open": {
    aliases: ["Blank_Open", "Blank-Open", "Blank open", "Blank-open"],
    allowed: true
  },
  "Final Form": { aliases: ["FF"], allowed: true },
  Invert: { aliases: ["Inverted"], allowed: true },
  Takeout: { aliases: ["Take out", "Take-out"], allowed: true },
  "Gob Cut": { aliases: ["Gob_Cut", "Gob-Cut", "Gob cut", "Gob-cut"], allowed: true },
  Rundown: { aliases: ["Rundown", "Run-down", "Run down", "Run_down"], allowed: false }
};

const normalizeSearchTokens = (text: string): string[] => {
  const normalized = text
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/&/g, " and ");
  return normalized.toLowerCase().match(/[a-z0-9]+/g) ?? [];
};

const coerceProductionSteps = (
  productionSteps: ProductionStepConfig | ProductionStepAliases | string[] | string
): Record<string, ProductionStepEntry> => {
  if (typeof productionSteps === "string") {
    return { [productionSteps]: { aliases: [productionSteps], allowed: true } };
  }
  if (Array.isArray(productionSteps)) {
    const map: Record<string, ProductionStepEntry> = {};
    productionSteps.forEach((step) => {
      map[step] = { aliases: [step], allowed: true };
    });
    return map;
  }

  const normalized: Record<string, ProductionStepEntry> = {};
  Object.entries(productionSteps).forEach(([canonical, config]) => {
    if (typeof config === "string") {
      normalized[canonical] = { aliases: [config], allowed: true };
      return;
    }
    if (Array.isArray(config)) {
      const list = [...config];
      if (!list.includes(canonical)) list.unshift(canonical);
      normalized[canonical] = { aliases: list, allowed: true };
      return;
    }
    const entry = config ?? {};
    const list = entry.aliases ? [...entry.aliases] : [];
    if (!list.includes(canonical)) list.unshift(canonical);
    normalized[canonical] = { aliases: list, allowed: entry.allowed ?? true };
  });
  return normalized;
};

const buildProductionStepIndex = (steps: Record<string, ProductionStepEntry>) => {
  const index = new Map<string, { allowed: boolean; aliases: AliasEntry[] }>();
  Object.entries(steps).forEach(([canonical, config]) => {
    const list = config.aliases;
    const entries: AliasEntry[] = list
      .map((alias) => {
        const tokens = normalizeSearchTokens(alias);
        if (!tokens.length) return null;
        const hasDigits = tokens.some((token) => /\d/.test(token));
        const compact = tokens.join("");
        return {
          tokens,
          hasDigits,
          tokenCount: tokens.length,
          charCount: compact.length
        };
      })
      .filter((entry): entry is AliasEntry => Boolean(entry));
    index.set(canonical, { allowed: config.allowed, aliases: entries });
  });
  return index;
};

const DEFAULT_STEPS = coerceProductionSteps(DEFAULT_PRODUCTION_STEP_CONFIG);
const DEFAULT_INDEX = buildProductionStepIndex(DEFAULT_STEPS);

const sequenceMatcherRatio = (a: string, b: string): number => {
  if (!a.length && !b.length) return 1;
  if (!a.length || !b.length) return 0;

  const matchSize = matchBlocksSize(a, b, 0, a.length, 0, b.length);
  return (2 * matchSize) / (a.length + b.length);
};

const matchBlocksSize = (
  a: string,
  b: string,
  alo: number,
  ahi: number,
  blo: number,
  bhi: number
): number => {
  const match = findLongestMatch(a, b, alo, ahi, blo, bhi);
  if (match.size === 0) return 0;

  return (
    match.size +
    matchBlocksSize(a, b, alo, match.a, blo, match.b) +
    matchBlocksSize(a, b, match.a + match.size, ahi, match.b + match.size, bhi)
  );
};

const findLongestMatch = (
  a: string,
  b: string,
  alo: number,
  ahi: number,
  blo: number,
  bhi: number
) => {
  let bestSize = 0;
  let bestA = alo;
  let bestB = blo;
  const bLen = bhi - blo;
  const row = new Array(bLen + 1).fill(0);

  for (let i = alo; i < ahi; i += 1) {
    let prev = 0;
    for (let j = 0; j < bLen; j += 1) {
      const temp = row[j + 1];
      if (a[i] === b[blo + j]) {
        const val = prev + 1;
        row[j + 1] = val;
        if (val > bestSize) {
          bestSize = val;
          bestA = i - val + 1;
          bestB = blo + j - val + 1;
        }
      } else {
        row[j + 1] = 0;
      }
      prev = temp;
    }
  }

  return { a: bestA, b: bestB, size: bestSize };
};

const scoreAliasMatch = (tokens: string[], aliasTokens: string[]): number => {
  if (!tokens.length || !aliasTokens.length) return 0;
  const aliasText = aliasTokens.join(" ");
  const aliasLen = aliasTokens.length;
  let best = 0;

  if (tokens.length < aliasLen) {
    best = Math.max(best, sequenceMatcherRatio(tokens.join(" "), aliasText));
  } else {
    for (let i = 0; i <= tokens.length - aliasLen; i += 1) {
      const window = tokens.slice(i, i + aliasLen);
      if (window.every((token, index) => token === aliasTokens[index])) {
        return 1;
      }
      best = Math.max(best, sequenceMatcherRatio(window.join(" "), aliasText));
    }
  }

  const aliasCompact = aliasTokens.join("");
  if (tokens.includes(aliasCompact)) {
    best = Math.max(best, 0.98);
  }

  return best;
};

const isBetterCandidate = (candidate: MatchCandidate, best: MatchCandidate) => {
  if (candidate.score !== best.score) return candidate.score > best.score;
  if (candidate.tokenCount !== best.tokenCount) return candidate.tokenCount > best.tokenCount;
  return candidate.charCount > best.charCount;
};

export const buildProductionStepConfigFromMapping = (
  rows: ProductionStepMappingRow[]
): ProductionStepConfig => {
  const config: ProductionStepConfig = {};
  rows.forEach((row) => {
    if (!row || !row.canonical_name) return;
    const aliases = Array.isArray(row.aliases)
      ? row.aliases.filter((alias) => typeof alias === "string" && alias.trim())
      : [];
    config[row.canonical_name] = {
      aliases,
      allowed: typeof row.allowed === "boolean" ? row.allowed : true
    };
  });
  return config;
};

export const matchProductionStep = (
  text: string,
  options?: {
    strictness?: number;
    productionSteps?: ProductionStepConfig | ProductionStepAliases | string[] | string;
  }
): { step: string; score: number; allowed: boolean } | null => {
  const strictness = options?.strictness ?? DEFAULT_STRICTNESS;
  const productionSteps = options?.productionSteps
    ? coerceProductionSteps(options.productionSteps)
    : DEFAULT_STEPS;
  const index = productionSteps === DEFAULT_STEPS ? DEFAULT_INDEX : buildProductionStepIndex(productionSteps);

  if (!index.size) return null;

  const tokens = normalizeSearchTokens(text);
  const alphaTokens = tokens.filter((token) => /^[a-z]+$/.test(token));
  let best: MatchCandidate = { step: "", score: 0, tokenCount: 0, charCount: 0 };

  for (const [canonical, entry] of index.entries()) {
    for (const alias of entry.aliases) {
      const tokensToUse = alias.hasDigits ? tokens : alphaTokens.length ? alphaTokens : tokens;
      const score = scoreAliasMatch(tokensToUse, alias.tokens);
      const candidate: MatchCandidate = {
        step: canonical,
        score,
        tokenCount: alias.tokenCount,
        charCount: alias.charCount
      };
      if (isBetterCandidate(candidate, best)) {
        best = candidate;
      }
    }
  }

  if (best.step && best.score >= strictness) {
    return { step: best.step, score: best.score, allowed: index.get(best.step)?.allowed ?? true };
  }
  return null;
};

export type { ProductionStepAliases, ProductionStepConfig };
